import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * CollapsibleFormWrap — v146.22
 *
 * Wraps a form-shaped block (lead_form / appointment / enquiry) so it
 * renders as a proper CTA button that expands to show the form on
 * click. Keeps bio pages clean when a creator has multiple forms.
 *
 * Behaviour:
 *   • On the public page (editable === false): collapsed by default,
 *     tapping the CTA reveals the form. Once expanded it stays open
 *     until the user submits or scrolls away.
 *   • In the editor (editable === true) OR when the block config
 *     explicitly sets `collapsible=false`, we render the form inline
 *     so the creator can preview the fields without extra clicks.
 *
 * Props:
 *   block       — the block object (used only for stable data-testids)
 *   editable    — bool; when true, form is always open
 *   ctaLabel    — big button label (e.g. "Book Appointment")
 *   ctaIcon     — optional Lucide icon component
 *   subtitle    — small copy under the CTA before it expands
 *   defaultOpen — start open even on the public page
 *   testid      — root data-testid
 *   children    — the form element(s)
 */
export default function CollapsibleFormWrap({
  block,
  editable = false,
  ctaLabel = "Open form",
  ctaIcon: Icon,
  subtitle,
  defaultOpen = false,
  testid,
  children,
}) {
  const collapsible = block?.config?.collapsible !== false; // default true
  const [open, setOpen] = useState(editable || defaultOpen || !collapsible);
  const forceOpen = editable || !collapsible;
  const showForm = forceOpen || open;

  if (forceOpen) {
    // Editor / inline mode — render the form directly.
    return (
      <div className="ah-block" data-testid={testid}>
        {children}
      </div>
    );
  }

  return (
    <div className="ah-block" data-testid={testid}>
      {!showForm && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${testid}-cta`}
          className="w-full inline-flex items-center justify-center gap-2 border-2 border-current px-4 py-3 font-black text-base uppercase tracking-wider transition-all hover:opacity-90 ah-collapsible-cta"
          style={{
            // v146.32 — Use explicit theme tokens instead of `currentColor`.
            // The old `background: currentColor` + `color: #fff` combo made
            // the browser resolve `currentColor` to the just-set text
            // color, rendering an invisible button in every theme where
            // `--ah-btn-bg` wasn't pre-set.
            background: "var(--ah-btn-bg, var(--ah-block-fg, #1a1a1a))",
            color: "var(--ah-btn-fg, var(--ah-block-bg, #ffffff))",
            boxShadow: "3px 3px 0 var(--ah-block-fg, currentColor)",
          }}
        >
          {Icon && <Icon className="w-4 h-4" strokeWidth={2.5} />}
          <span>{ctaLabel}</span>
          <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
        </button>
      )}
      {!showForm && subtitle && (
        <p className="text-xs ah-muted text-center mt-1.5">{subtitle}</p>
      )}
      {showForm && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="font-bold text-sm">{ctaLabel}</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-bold underline underline-offset-2 opacity-70 hover:opacity-100"
              data-testid={`${testid}-collapse`}
            >
              Close
            </button>
          </div>
          {children}
        </div>
      )}
    </div>
  );
}
